import { inject, Signal } from '@angular/core';

import {
  injectMutation,
  injectQuery,
  QueryClient,
} from '@tanstack/angular-query-experimental';

import { firstValueFrom } from 'rxjs';

import { TranslateService } from '@ngx-translate/core';

import { ToastService } from '@services/toast.service';
import { OutputSettings } from 'src/app/core/models/output-settings.models';
import {
  AdminOutputSettings,
  AdminOutputSettingsCopy,
  AdminOutputSettingsUpdate,
  OutputSettingsApiService,
} from '../services/output-settings-api.service';
import { HttpErrorResponse } from '@angular/common/http';
import { UtilsDataObjectApiService } from '../services/utils-data-object-api.service';

export interface OutputSettingsScope {
  objectId: string;
  objectClassId: number;
}

export interface OutputSettingsRecordParams extends OutputSettingsScope {
  outputSettingsId: number;
}

const OUTPUT_SETTINGS_LIST_ROOT_KEY = [
  'utils_data_object',
  'list_output_settings',
];

export const outputSettingsListQueryKey = (
  scope: OutputSettingsScope | null,
) => [
  ...OUTPUT_SETTINGS_LIST_ROOT_KEY,
  {
    objectId: scope?.objectId ?? null,
    objectClassId: scope?.objectClassId ?? null,
  },
];

/**
 * Output settings available for an object (GET /utils_data_object/list_output_settings).
 * Consumers pass a signal because the scope is decided asynchronously (from a route
 * input or the current object) - the query stays disabled until it is set.
 */
export function injectOutputSettingsListQuery(
  scope: Signal<OutputSettingsScope | null>,
) {
  const utilsDataObjectApi = inject(UtilsDataObjectApiService);

  return injectQuery(() => ({
    queryKey: outputSettingsListQueryKey(scope()),
    queryFn: () =>
      firstValueFrom(
        utilsDataObjectApi.getOutputSettingsList(
          scope().objectClassId,
          scope().objectId,
        ),
      ),
    enabled: !!scope(),
  }));
}

/**
 * One full output-settings record (GET /output_settings/record), returned as a
 * promise because every consumer fetches it imperatively: a click on edit/clone,
 * then a dialog opens with the result. `fetchQuery` is the sanctioned imperative
 * tier and it dedupes double clicks.
 */
export function injectFetchOutputSettingsRecord() {
  const outputSettingsApi = inject(OutputSettingsApiService);
  const queryClient = inject(QueryClient);

  return ({
    outputSettingsId,
    objectId,
    objectClassId,
  }: OutputSettingsRecordParams) =>
    queryClient.fetchQuery({
      queryKey: [
        'output_settings',
        'record',
        outputSettingsId,
        { objectId, objectClassId },
      ],
      queryFn: () =>
        firstValueFrom(
          outputSettingsApi.getOutputSettingsRecord(
            outputSettingsId,
            objectId,
            objectClassId,
          ),
        ),
    });
}

export interface SaveOutputSettingsInput extends OutputSettingsScope {
  outputSettings: OutputSettings;
}

/**
 * Save an output-settings record (POST /output_settings/save). `onSuccess` upserts
 * the saved record into **every** cached list and then invalidates them, so open
 * dropdowns show the new entry immediately and still reconcile with the server.
 */
export function injectSaveOutputSettingsMutation() {
  const outputSettingsApi = inject(OutputSettingsApiService);
  const queryClient = inject(QueryClient);
  const toast = inject(ToastService);
  const translate = inject(TranslateService);

  return injectMutation(() => ({
    mutationFn: ({
      objectId,
      objectClassId,
      outputSettings,
    }: SaveOutputSettingsInput) =>
      firstValueFrom(
        outputSettingsApi.saveOutputSettings(
          objectId,
          objectClassId,
          outputSettings,
        ),
      ),
    onSuccess: (saved) => {
      queryClient.setQueriesData<OutputSettings[]>(
        { queryKey: OUTPUT_SETTINGS_LIST_ROOT_KEY },
        (list) => upsertOutputSettings(list, saved),
      );
      queryClient.invalidateQueries({
        queryKey: OUTPUT_SETTINGS_LIST_ROOT_KEY,
      });

      toast.showSuccess(
        translate.instant(
          'output-settings-popup.msg.output-settings-saved-successfully',
        ),
      );
    },
    onError: () =>
      toast.showError(
        translate.instant(
          'output-settings-popup.msg.error-saving-output-settings',
        ),
      ),
  }));
}

/**
 * `count_used: 0` is a placeholder for the optimistic entry - the invalidation
 * right after this replaces it with server truth.
 */
function upsertOutputSettings(
  list: OutputSettings[] | undefined,
  saved: OutputSettings,
): OutputSettings[] | undefined {
  if (!list || !saved) {
    return list;
  }

  const entry: OutputSettings = { ...saved, count_used: 0 };
  const index = list.findIndex((item) => item.id === saved.id);

  if (index === -1) {
    return [...list, entry];
  }

  return list.map((item, i) => (i === index ? entry : item));
}

// * Admin -> Output Settings grid (WAP-1638) ================================
// Rows live in ag-grid's SSRM block cache and the Solr index is refreshed by an
// async worker, so the hooks own the toasts and the grid patches its row from the
// response in a per-call callback instead of re-querying.

/** Rename / describe a managed row: POST /output_settings/admin_update. */
export function injectAdminUpdateOutputSettingsMutation() {
  const api = inject(OutputSettingsApiService);
  const toast = inject(ToastService);
  const translate = inject(TranslateService);

  return injectMutation(() => ({
    mutationFn: (update: AdminOutputSettingsUpdate) =>
      firstValueFrom(api.adminUpdateOutputSettings(update)),
    onSuccess: () =>
      toast.showSuccess(
        translate.instant(
          'output-settings-grid.msg.the-output-settings-were-renamed',
        ),
      ),
    onError: () =>
      toast.showError(
        translate.instant(
          'output-settings-grid.msg.renaming-the-output-settings-failed',
        ),
      ),
  }));
}

/** Copy a row into a new named list: POST /output_settings/admin_copy. */
export function injectAdminCopyOutputSettingsMutation() {
  const api = inject(OutputSettingsApiService);
  const toast = inject(ToastService);
  const translate = inject(TranslateService);

  return injectMutation(() => ({
    mutationFn: (copy: AdminOutputSettingsCopy) =>
      firstValueFrom(api.adminCopyOutputSettings(copy)),
    onSuccess: () =>
      toast.showSuccess(
        translate.instant(
          'output-settings-grid.msg.the-output-settings-were-copied',
        ),
      ),
    onError: () =>
      toast.showError(
        translate.instant(
          'output-settings-grid.msg.copying-the-output-settings-failed',
        ),
      ),
  }));
}

/**
 * Soft-delete a named list: DELETE /output_settings/admin_delete/{id}. The
 * backend answers 409 while objects still point at the row (and always for a
 * company default) - that case gets its own message.
 */
export function injectAdminDeleteOutputSettingsMutation() {
  const api = inject(OutputSettingsApiService);
  const toast = inject(ToastService);
  const translate = inject(TranslateService);

  return injectMutation(() => ({
    mutationFn: (row: AdminOutputSettings) =>
      firstValueFrom(api.adminDeleteOutputSettings(row.id)),
    onSuccess: () =>
      toast.showSuccess(
        translate.instant(
          'output-settings-grid.msg.the-output-settings-were-deleted',
        ),
      ),
    onError: (error: HttpErrorResponse, row) =>
      toast.showError(
        error?.status === 409
          ? translate.instant(
              'output-settings-grid.msg.still-used-by-objects-re-point-them-first',
              { count: row.count_used },
            )
          : translate.instant(
              'output-settings-grid.msg.deleting-the-output-settings-failed',
            ),
      ),
  }));
}
